import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { ReliabilityBadge } from '../components/ReliabilityBadge';

const RELIABILITY_LEVELS = ['verified_fact', 'testimonial', 'opinion', 'hypothesis', 'interpretation', 'unverified_data'];

export default function Library() {
  const { t, lang } = useI18n();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [reliability, setReliability] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [aRes, cRes] = await Promise.all([
          api.get('/library/articles'),
          api.get('/library/categories'),
        ]);
        setArticles(aRes.data);
        setCategories(cRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return articles.filter((a) => {
      if (category && a.category !== category) return false;
      if (reliability && a.reliability !== reliability) return false;
      if (q) {
        const s = q.toLowerCase();
        const hay = [a.title_it, a.title_en, a.excerpt_it, a.excerpt_en, ...(a.tags || [])].join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [articles, q, category, reliability]);

  const catLabel = (key) => {
    const c = categories.find((c) => c.key === key);
    return c ? (lang === 'it' ? c.label_it : c.label_en) : key;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">Tikkun</p>
      <h1 className="font-display text-5xl md:text-6xl font-medium tracking-tight leading-tight">{t.library.title}</h1>
      <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{t.library.subtitle}</p>

      {/* Filters */}
      <div className="mt-12 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.library.search}
            data-testid="library-search-input"
            className="w-full pl-11 pr-4 py-3 rounded-full bg-secondary border border-transparent focus:border-primary focus:bg-background focus:ring-1 focus:ring-primary outline-none text-sm transition-colors"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="library-category-filter"
          className="px-4 py-3 rounded-full bg-secondary border border-transparent focus:border-primary focus:bg-background outline-none text-sm min-w-[180px]"
        >
          <option value="">{t.library.all_categories}</option>
          {categories.map((c) => (
            <option key={c.key} value={c.key}>{lang === 'it' ? c.label_it : c.label_en}</option>
          ))}
        </select>
        <select
          value={reliability}
          onChange={(e) => setReliability(e.target.value)}
          data-testid="library-reliability-filter"
          className="px-4 py-3 rounded-full bg-secondary border border-transparent focus:border-primary focus:bg-background outline-none text-sm min-w-[200px]"
        >
          <option value="">{t.library.all_reliabilities}</option>
          {RELIABILITY_LEVELS.map((r) => (
            <option key={r} value={r}>{t.reliability[r]}</option>
          ))}
        </select>
      </div>

      {/* Results */}
      <div className="mt-12" data-testid="library-results">
        {loading ? (
          <p className="text-muted-foreground">{t.common.loading}</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground text-lg py-16 text-center">{t.library.empty}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <Link
                key={a.id}
                to={`/library/${a.id}`}
                data-testid={`article-card-${a.id}`}
                className="group flex flex-col p-7 rounded-3xl border border-border bg-card hover:-translate-y-1 hover:shadow-xl hover:shadow-black/5 transition-all duration-300"
              >
                <div className="flex items-center justify-between mb-4">
                  <ReliabilityBadge level={a.reliability} />
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock size={12} /> {a.read_time_min} {t.library.min}</span>
                </div>
                <span className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{catLabel(a.category)}</span>
                <h3 className="font-display text-xl font-medium leading-snug tracking-tight text-balance">
                  {lang === 'it' ? a.title_it : a.title_en}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                  {lang === 'it' ? a.excerpt_it : a.excerpt_en}
                </p>
                <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{a.author}</span>
                  <span className="inline-flex items-center gap-1 text-brand-deep dark:text-brand-gold-dark font-medium group-hover:gap-2 transition-all">
                    {t.library.read} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
